<template>
    <cod-dialog
        ref="dialog"
        :show.sync="showDialog"
        :drawer="drawer"
        back-icon="fal fa-arrow-left"
    >
        <!-- Header -->
        <div slot="header" class="header">
            <div>{{ $tc('general.buyer', 2) }}</div>

            <v-btn
                class="mt-1"
                text
                @click="openClientForm()"
            >
                <v-icon class="mr-3">fal fa-plus</v-icon>
                {{ $t('general.add') }}
            </v-btn>
        </div>

        <!-- Content -->
        <div slot="content" class="select-client-content">
            <div>
                <v-text-field
                    v-model="filterQuery"
                    class="mx-4"
                    :placeholder="$t('general.search')"
                    prepend-inner-icon="far fa-search"
                    clearable
                    clear-icon="fal fa-times"
                />
            </div>

            <client-list
                ref="clientList"
                class="flex-1"
                :filter-query.sync="filterQuery"
                :selected-client.sync="selectedClient"
                @edit-client="openClientForm($event)"
                @add-payment="openPaymentForm($event)"
            />
        </div>

        <!-- Footer -->
        <v-row
            slot="footer"
            class="select-client-footer"
            justify="space-between"
        >
            <v-btn
                class="flex-1 mr-1"
                color="primary"
                outlined
                @click="showDialog = false"
            >
                {{ $t('general.close') }}
            </v-btn>

            <v-btn
                v-if="curOrder"
                class="flex-1 ml-1"
                color="primary"
                depressed
                @click="select"
            >
                {{ $t('general.confirm') }}
            </v-btn>
        </v-row>

        <!-- Drawer -->
        <div slot="drawer">
            <template v-if="drawer === 'clientForm'">
                <v-toolbar
                    class="wfull"
                    color="#1565c0"
                    absolute
                    dark
                    flat
                >
                    <v-btn
                        icon
                        dark
                        @click="drawer = null"
                    >
                        <v-icon>fal fa-arrow-left</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        {{ $tc('general.buyer', 1) }}
                    </v-toolbar-title>
                </v-toolbar>

                <div class="drawer-content">
                    <client-form
                        :client="editClient"
                        @save="onClientSaved"
                    />
                </div>
            </template>

            <template v-if="drawer === 'paymentForm'">
                <v-toolbar
                    class="wfull"
                    color="#1565c0"
                    absolute
                    dark
                    flat
                >
                    <v-btn
                        icon
                        dark
                        @click="drawer = null"
                    >
                        <v-icon>fal fa-arrow-left</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        {{ $t('general.installments_pay') }}
                    </v-toolbar-title>
                </v-toolbar>

                <div v-if="editClient" class="drawer-content d-flex justify-center">
                    <div class="pa-7">
                        <v-text-field
                            ref="textField"
                            v-model="paymentAmount"
                            class="text-field"
                            :suffix="$t('general.amount')"
                            reverse
                            readonly
                        />

                        <div class="d-flex justify-space-between mt-1 mb-6">
                            <div>{{ $t('general.installments_remaining') }}:</div>
                            <div>
                                {{ editClient.getDebt().subtract(Money.parse(paymentAmount)).toFormat('0.00') }}
                            </div>
                        </div>

                        <numpad
                            ref="numpad"
                            v-model="paymentAmount"
                            class="numpad"
                            :precision="2"
                            :sign-button-enabled="false"
                            :ok-button-enabled="paymentAmount > 0"
                            :max="editClient.debt"
                            :dark="false"
                            @confirm="onPaymentAdded"
                        />
                    </div>
                </div>
            </template>
        </div>

    </cod-dialog>
</template>

<script>
import ApiService from '@/services/ApiService'
import CodDialog from '@/components/CodDialog';
import ClientList from '@/views/shared/components/ClientList';
import ClientForm from '@/views/shared/components/ClientForm';
import Client from '@/models/Client';
import Numpad from '@/components/Numpad';
import HtmlGen from '@/wrappers/HtmlGen';
import { mapGetters } from 'vuex';

export default {
    components: {
        CodDialog,
        ClientList,
        ClientForm,
        Numpad
    },

    props: {
        show: {
            type: Boolean,
            default: false
        },
        clientId: {
            type: Number,
            default: null
        }
    },

    data: () => ({
        showDialog: false,
        selectedClient: null,
        editClient: null,
        filterQuery: null,
        drawer: null,

        paymentAmount: '0',
    }),

    watch: {
        show(val) {
            this.showDialog = val;
            this.drawer = null;

            if (val) {
                this.selectedClient = Client.find(this.clientId);
            }

            if (val) {
                this.filterQuery = null;
                this.$nextTick(() => {
                    this.$refs.clientList.filter();
                    this.$refs.dialog.$refs.content.scrollTop = 0;
                });
            }
        },

        showDialog(val) {
            this.$emit('update:show', val);
        }
    },

    computed: {
        ...mapGetters(['curOrder']),
    },

    methods: {
        openClientForm(client = null) {
            this.editClient = client
            this.drawer = 'clientForm'
        },

        openPaymentForm(client = null) {
            if(!this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error');
                return
            }

            this.paymentAmount = '0'
            this.editClient = client
            this.drawer = 'paymentForm'
        },

        select() {
            this.showDialog = false;
            this.$emit('select', this.selectedClient);
        },

        onClientSaved(client) {
            this.selectedClient = client
            this.drawer = null
        },

        onPaymentAdded(value) {
            if(!this.isOnline) {
                this.$snackbar(this.$t('connection.internet_offline_message_not_allowed'), 'error');
                return
            }

            this.$confirm(
                this.$t('general.are_you_sure'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.installments_pay')
                },
                result => {
                    if (!result) return;

                    ApiService.patch(`/clients/${this.editClient.id}/payment`, { payment: value })
                        .then(response => {
                            this.editClient.addPayment(value)

                            const dateFormat = ['me', 'sr', 'ba'].includes(locale) ? 'D MMM HH:mm' : 'D MMM HH:mm'

                            const printText = `\n${this.$tc('general.buyer', 1)}: ${this.editClient.name}\n\n` +
                                `${this.$t('general.installments_paid')}: ${Money.parse(value).toFormat('0.00')}\n` +
                                `${this.$t('general.installments_remaining')}: ${this.editClient.getDebt().toFormat('0.00')}\n` +
                                `${this.$t('general.time')}: ${moment().format(dateFormat)}\n\n\n` +
                                `${this.$tc('general.cashier', 1)}:\n\n________________________________\n\n\n` +
                                `${this.$tc('general.buyer', 1)}:\n\n________________________________\n\n\n`

                            const printHtml = printText.replace(/(?:\r\n|\r|\n)/g, '<br>') +
                                HtmlGen.qrCodeToHtml(printText);

                            this.$printService.printCustom(printText, printHtml, printText)

                            setTimeout(() => this.$printService.printCustom(printText, printHtml, printText), 2000)

                            this.editClient = null
                            this.drawer = null

                            Client.update(response.data.client)

                            this.$snackbar(this.$t('general.installments_paid'), 'success');
                        })
                }
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.select-client-content {
    height: 610px;
    display: flex;
    flex-direction: column;
}

.select-client-footer {
    padding: 16px;
}

.drawer-content {
    max-height: 100%;
    overflow-y: auto;
    padding-top: 64px;

    .text-field {
        color: #3f4456;
        pointer-events: none;
        font-size: 22px;

        .v-input__slot:before {
            border-color: #3f4456;
        }

        .v-input__slot:after {
            border: none !important;
        }

        input {
            color: #2196f3;
        }
    }

    .numpad {
        max-width: 360px;
    }
}
</style>
